// src/components/tiptap/extensions/PracticeBingoNode.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer, NodeViewWrapper } from "@tiptap/react";
import CelebrationOverlay from "../../common/CelebrationOverlay.jsx";
import { supabase } from "../../../config/supabase";
import * as FiIcons from "react-icons/fi";
import SafeIcon from "../../../common/SafeIcon.jsx";
import ConfirmModal from "../../common/ConfirmModal.jsx";

const {
  FiGrid,
  FiSave,
  FiTrash2,
  FiRefreshCw,
  FiSettings,
  FiCheckCircle,
  FiAward,
  FiLock,
  FiX,
  FiVolume2,
  FiVolumeX,
} = FiIcons;

const cls = (...xs) => xs.filter(Boolean).join(" ");

/* helper to allow form controls to keep default behavior */
const isFormControl = (el) =>
  el &&
  (el.tagName === "SELECT" ||
    el.tagName === "INPUT" ||
    el.tagName === "TEXTAREA" ||
    el.isContentEditable);

const DEFAULT_SETTINGS = {
  gridSize: 5,
  includeFreeCenter: true,
  instrument: "", // optional filter
  categoryIds: [], // array<uuid>
  tagsAny: [], // array<string> (optional; can expose later)
};

function stableJsonStringify(v) {
  try {
    return JSON.stringify(v ?? {});
  } catch {
    return "";
  }
}

const CELEBRATION_SFX_URL =
  "https://vdjxrusqywiraonfwujq.supabase.co/storage/v1/object/public/mmsmedia/audio/game-effects/celebration.mp3";

// One audio instance per page for Bingo celebration
const sharedBingoSfx = {
  ready: false,
  celebration: null,
  initPromise: null,
};

async function ensureBingoSfx() {
  if (sharedBingoSfx.ready) return;
  if (sharedBingoSfx.initPromise) return sharedBingoSfx.initPromise;

  sharedBingoSfx.initPromise = (async () => {
    try {
      const a = new Audio(CELEBRATION_SFX_URL);
      a.preload = "auto";
      sharedBingoSfx.celebration = a;
    } catch {
      sharedBingoSfx.celebration = null;
    }
    sharedBingoSfx.ready = true;
  })();

  return sharedBingoSfx.initPromise;
}

function playBingoCelebrationSfx(muted) {
  if (muted) return;
  const a = sharedBingoSfx.celebration;
  if (!a) return;
  try {
    a.currentTime = 0;
    const p = a.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
  } catch {}
}

function popAndWiggle(el) {
  // WAAPI: small “new sticker” celebration
  try {
    el?.animate?.(
      [
        { transform: "scale(1) rotate(0deg)" },
        { transform: "scale(1.12) rotate(6deg)", offset: 0.35 },
        { transform: "scale(1.03) rotate(-6deg)", offset: 0.65 },
        { transform: "scale(1) rotate(0deg)" },
      ],
      { duration: 520, easing: "cubic-bezier(.22,1,.36,1)" }
    );
  } catch {}
}

function cardWiggle(el) {
  // WAAPI: subtle "checked!" wiggle for the clicked card
  try {
    el?.animate?.(
      [
        { transform: "translateY(0px) rotate(0deg) scale(1)" },
        { transform: "translateY(-2px) rotate(-1.5deg) scale(1.02)", offset: 0.25 },
        { transform: "translateY(1px) rotate(1.5deg) scale(1.03)", offset: 0.55 },
        { transform: "translateY(0px) rotate(0deg) scale(1)" },
      ],
      { duration: 360, easing: "cubic-bezier(.22,1,.36,1)" }
    );
  } catch {}
}

function safeUuid() {
  // Browser-safe fallback
  // @ts-ignore
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : String(Date.now());
}

/* ─────────────────────────────────────────────────────────────
   Light cache for categories (same pattern as MemoryMatch)
   ──────────────────────────────────────────────────────────── */
let bingoCategoriesCache = null; // {id,name}[]
let bingoCategoriesInflight = null;

let bingoInstrumentsCache = null; // string[]
let bingoInstrumentsInflight = null;

async function fetchBingoCategoriesCached() {
  if (Array.isArray(bingoCategoriesCache)) return bingoCategoriesCache;
  if (bingoCategoriesInflight) return bingoCategoriesInflight;

  bingoCategoriesInflight = (async () => {
    const { data, error } = await supabase
      .from("practice_bingo_categories")
      .select("id,name")
      .order("sort_order", { ascending: true, nullsFirst: false });

    const rows = error ? [] : data || [];
    // Fallback sort if sort_order absent
    rows.sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));

    bingoCategoriesCache = rows;
    bingoCategoriesInflight = null;
    return rows;
  })();

  return bingoCategoriesInflight;
}

async function fetchBingoInstrumentsCached({ schoolId }) {
  if (Array.isArray(bingoInstrumentsCache)) return bingoInstrumentsCache;
  if (bingoInstrumentsInflight) return bingoInstrumentsInflight;

  bingoInstrumentsInflight = (async () => {
    let q = supabase
      .from("practice_bingo_prompts")
      .select("instrument")
      .eq("is_active", true);

    // Tenant-safe: prefer school scoped instruments (+ global/null)
    if (schoolId) q = q.or(`school_id.is.null,school_id.eq.${schoolId}`);

    const { data, error } = await q;
    const rows = error ? [] : data || [];

    const list = Array.from(
      new Set(rows.map((r) => (r.instrument || "").trim()).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b));

    bingoInstrumentsCache = list;
    bingoInstrumentsInflight = null;
    return list;
  })();

  return bingoInstrumentsInflight;
}

/* ─────────────────────────────────────────────────────────────
   Bingo line detection
   - Counts completed lines: rows + cols + diagonals
   - Level examples:
     - 1+ line => Level 1
     - 2+ lines => Level 2
     - full card => Level 3
   ──────────────────────────────────────────────────────────── */
function computeBingoStatus({ gridSize, includeFreeCenter, checkedPositions }) {
  const n = gridSize;
  const total = n * n;
  const centerPos = total % 2 === 1 ? Math.floor(total / 2) : null;

  const isChecked = (pos) => {
    if (includeFreeCenter && centerPos === pos) return true;
    return checkedPositions.has(pos);
  };

  const lines = [];

  // rows
  for (let r = 0; r < n; r++) {
    const row = [];
    for (let c = 0; c < n; c++) row.push(r * n + c);
    lines.push(row);
  }

  // cols
  for (let c = 0; c < n; c++) {
    const col = [];
    for (let r = 0; r < n; r++) col.push(r * n + c);
    lines.push(col);
  }

  // diagonals
  const d1 = [];
  const d2 = [];
  for (let i = 0; i < n; i++) {
    d1.push(i * n + i);
    d2.push(i * n + (n - 1 - i));
  }
  lines.push(d1, d2);

  let completedLines = 0;
  for (const line of lines) {
    const ok = line.every((pos) => isChecked(pos));
    if (ok) completedLines += 1;
  }

  const fullCard = (() => {
    for (let pos = 0; pos < total; pos++) {
      if (!isChecked(pos)) return false;
    }
    return true;
  })();

  const level =
    fullCard ? 3 : completedLines >= 2 ? 2 : completedLines >= 1 ? 1 : 0;

  return { completedLines, fullCard, level };
}

function PracticeBingoBlock({ node, updateAttributes, editor, deleteNode }) {
  const attrs = node.attrs || {};
  const ctx = editor?.storage?.mms ?? {};

  const saved = !!attrs.recordId;
  const canEditTool = !!ctx?.userId && !!editor?.isEditable; // teachers only
  const canDelete = canEditTool;

  // Ensure attrs exist once
  useEffect(() => {
    if (!attrs.noteBlockId) {
      updateAttributes({
        noteBlockId: safeUuid(),
        settings: DEFAULT_SETTINGS,
      });
    }
    if (!attrs.settings) {
      updateAttributes({ settings: DEFAULT_SETTINGS });
    }
  }, []);

  const effectiveLessonNoteId = attrs.lessonNoteId ?? ctx.lessonNoteId ?? null;
  const effectiveSchoolId = attrs.schoolId ?? ctx.schoolId ?? null;
  const effectiveStudentMasterId =
    attrs.studentMasterId ?? ctx.masterStudentId ?? null;

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [confirm, setConfirm] = useState(null); // {title,message,onConfirm,confirmText,confirmButtonClass}
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [categories, setCategories] = useState([]);
  const [instruments, setInstruments] = useState([]);
  const [settings, setSettings] = useState(() => attrs.settings || DEFAULT_SETTINGS);
  const [sfxMuted, setSfxMuted] = useState(!!attrs.sfxMuted);

  const [cells, setCells] = useState([]); // [{cell_id, cell_position, title, is_free, checked_at}]
  const [loadError, setLoadError] = useState("");

  const lastCelebratedLevelRef = useRef(0);
  const lastCelebratedLinesRef = useRef(0);
  const newestStickerRef = useRef(null);

  const [stickers, setStickers] = useState([]); // [{id?, imageUrl, label}]
  const [stickerLightbox, setStickerLightbox] = useState(null); // {imageUrl,label} | null
  const [celebrationOpen, setCelebrationOpen] = useState(false);
  const [celebrationMsg, setCelebrationMsg] = useState("");
  const seededCelebrationBaselinesRef = useRef(false);

  // Keep attrs.settings in sync with local settings when teacher changes it
  const persistSettingsToAttrs = (next) => {
    setSettings(next);
    updateAttributes({ settings: next });
  };

  const buildPayloadSettings = () => ({
    ...DEFAULT_SETTINGS,
    ...(settings || {}),
    gridSize,
    includeFreeCenter,
  });

  // Categories (cached)
  useEffect(() => {
    let alive = true;
    (async () => {
      const rows = await fetchBingoCategoriesCached();
      if (!alive) return;
      setCategories(rows);
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Instruments (cached)
  useEffect(() => {
    let alive = true;
    (async () => {
      const rows = await fetchBingoInstrumentsCached({ schoolId: effectiveSchoolId });
      if (!alive) return;
      setInstruments(rows);
    })();
    return () => {
      alive = false;
    };
  }, [effectiveSchoolId]);

  // Preload celebration SFX (best-effort)
  useEffect(() => {
    ensureBingoSfx().catch(() => {});
  }, []);

  const gridSize = Math.max(1, Math.min(7, Number(settings?.gridSize) || 5));
  const includeFreeCenter =
    (settings?.includeFreeCenter ?? true) && (gridSize * gridSize) % 2 === 1;

  const checkedPositions = useMemo(() => {
    const s = new Set();
    for (const c of cells) {
      if (c?.checked_at) s.add(Number(c.cell_position));
    }
    return s;
  }, [cells]);

  const hasProgress = checkedPositions.size > 0;
  // Once student starts ticking, lock settings/regenerate permanently for this instance
  const settingsLocked = saved && hasProgress;

  const status = useMemo(() => {
    return computeBingoStatus({
      gridSize,
      includeFreeCenter,
      checkedPositions,
    });
  }, [gridSize, includeFreeCenter, checkedPositions]);

  // IMPORTANT: Seed celebration baselines from persisted progress so we don't
  // (a) spam SFX/confetti on page refresh and
  // (b) re-open sticker modal for already-earned milestones.
  useEffect(() => {
    if (!saved) return;
    if (seededCelebrationBaselinesRef.current) return;
    // Wait until cells have loaded (otherwise status would be 0/0 and we'd seed wrong)
    if (!Array.isArray(cells) || cells.length === 0) return;

    lastCelebratedLinesRef.current = status.completedLines || 0;
    lastCelebratedLevelRef.current = status.level || 0;
    seededCelebrationBaselinesRef.current = true;
  }, [saved, cells, status.completedLines, status.level]);

  // Celebrate ONLY on the same milestones that stickers are awarded:
  // level 1 (first line), level 2 (two lines), level 3 (full card)
  // Also fire sticker completion event when level increases (best-effort).
  useEffect(() => {
    if (!saved) return;
    // Don't emit stickers on page load; only on true progress transitions.
    if (!seededCelebrationBaselinesRef.current) return;
    const prev = lastCelebratedLevelRef.current || 0;
    if (status.level > prev) {
      lastCelebratedLevelRef.current = status.level;

      // Confetti + SFX aligned to sticker milestones
      const msg =
        status.level === 3
          ? "Full card complete!"
          : status.level === 2
          ? "Two lines complete!"
          : "Bingo!";
      setCelebrationMsg(msg);
      setCelebrationOpen(true);
      playBingoCelebrationSfx(sfxMuted);

      const earnedFor =
        status.level === 3
          ? "Bingo: Full card!"
          : status.level === 2
          ? "Bingo: Two lines!"
          : "Bingo: One line!";

      window.dispatchEvent(
        new CustomEvent("mms:toolCompleted", {
          detail: { noteBlockId: attrs.noteBlockId, earnedFor },
        })
      );
    }
  }, [status.level, saved, sfxMuted, attrs.noteBlockId]);

  const loadCells = async (instanceId) => {
    setLoadError("");
    const { data, error } = await supabase
      .from("practice_bingo_instance_cells")
      .select("id, position, prompt_title_snapshot, is_free, checked_at")
      .eq("instance_id", instanceId)
      .order("position", { ascending: true });

    if (error) {
      setLoadError(error.message || "Failed to load bingo grid.");
      setCells([]);
      return;
    }

    const mapped = (data || []).map((r) => ({
      cell_id: r.id,
      cell_position: r.position,
      title: r.prompt_title_snapshot,
      is_free: !!r.is_free,
      checked_at: r.checked_at,
    }));

    setCells(mapped);
  };

  // On mount: if saved, load cells (supports viewing existing tools)
  useEffect(() => {
    if (!attrs.recordId) return;
    loadCells(attrs.recordId).catch(() => {});
  }, [attrs.recordId]);

  // Stickers: best-effort hydrate + live append on event
  useEffect(() => {
    let alive = true;

    async function loadStickersBestEffort() {
      if (!saved) return;
      if (!attrs?.noteBlockId) return;
      try {
        const { data, error } = await supabase
          .from("student_stickers")
          .select("id, sticker_label_snapshot, sticker_image_url_snapshot, created_at")
          .eq("note_block_id", attrs.noteBlockId)
          .order("created_at", { ascending: true });
        if (!alive) return;
        if (error) return;
        const mapped =
          (data || []).map((r) => ({
            id: r.id,
            label: r.sticker_label_snapshot || "",
            imageUrl: r.sticker_image_url_snapshot || "",
          })) || [];
        setStickers(mapped.filter((s) => s.imageUrl));
      } catch {
        // Ignore: schema mismatch or RLS; the live event will still attach new stickers
      }
    }

    const onStickerAdded = (e) => {
      const d = e?.detail || {};
      if (!d?.noteBlockId || d.noteBlockId !== attrs?.noteBlockId) return;
      if (!d?.imageUrl) return;
      setStickers((prev) => {
        // Avoid dupes
        if (prev.some((s) => s.imageUrl === d.imageUrl && (s.label || "") === (d.label || "")))
          return prev;
        return [...prev, { imageUrl: d.imageUrl, label: d.label || "" }];
      });
      // animate newest after render
      requestAnimationFrame(() => popAndWiggle(newestStickerRef.current));
    };

    loadStickersBestEffort();
    window.addEventListener("mms:sticker-added", onStickerAdded);
    return () => {
      alive = false;
      window.removeEventListener("mms:sticker-added", onStickerAdded);
    };
  }, [saved, attrs?.noteBlockId]);

  const persistInstanceSettings = async (instanceId) => {
    if (!instanceId) return;
    const payloadSettings = buildPayloadSettings();

    // keep node attrs in sync too
    updateAttributes({ settings: payloadSettings });

    const { error } = await supabase
      .from("practice_bingo_instances")
      .update({ settings: payloadSettings })
      .eq("id", instanceId);

    if (error) throw error;
  };

  const handleSave = async () => {
    if (!canEditTool) return;

    if (!effectiveLessonNoteId) {
      alert("Missing context: lessonNoteId is required.");
      return;
    }
    if (!effectiveSchoolId) {
      alert("Missing context: schoolId is required.");
      return;
    }
    if (!effectiveStudentMasterId) {
      alert("Missing context: studentMasterId is required.");
      return;
    }

    setSaving(true);

    const nbid = attrs.noteBlockId ?? safeUuid();
    const payloadSettings = buildPayloadSettings();

    const { data, error } = await supabase.rpc(
      "get_or_create_practice_bingo_instance",
      {
        p_school_id: effectiveSchoolId,
        p_student_master_id: effectiveStudentMasterId,
        p_note_block_id: nbid,
        p_lesson_note_id: effectiveLessonNoteId,
        p_settings: payloadSettings,
      }
    );

    setSaving(false);
    if (error) return alert(error.message);

    updateAttributes({
      recordId: data.id,
      noteBlockId: nbid,
      lessonNoteId: effectiveLessonNoteId,
      schoolId: effectiveSchoolId,
      studentMasterId: effectiveStudentMasterId,
      settings: payloadSettings,
    });

    // Generate immediately after save (first-time)
    await handleGenerate(data.id, false);
  };

  const handleGenerate = async (instanceId, regenerate) => {
    if (settingsLocked) return;
    setGenerating(true);
    setLoadError("");
    try {
      const { data, error } = await supabase.rpc("generate_practice_bingo_card", {
        p_instance_id: instanceId,
        p_regenerate: !!regenerate,
      });
      if (error) throw error;

      // RPC returns rows. Prefer RPC result; fall back to loadCells.
      const mapped = (data || []).map((r) => ({
        cell_id: r.cell_id,
        cell_position: r.cell_position,
        title: r.title,
        is_free: !!r.is_free,
        checked_at: r.checked_at,
      }));

      if (mapped.length) setCells(mapped);
      else await loadCells(instanceId);

      // Reset level celebration baseline on regenerate
      if (regenerate) {
        lastCelebratedLevelRef.current = 0;
        lastCelebratedLinesRef.current = 0;
        seededCelebrationBaselinesRef.current = false;
      }
    } catch (e) {
      setLoadError(e?.message || "Failed to generate bingo card.");
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async () => {
    if (!canDelete) return;

    if (!attrs.recordId) {
      deleteNode?.();
      return;
    }

    setConfirm({
      title: "Delete Practice Bingo?",
      message:
        "This will remove the bingo tool from the note and delete its saved progress.",
      confirmText: "Delete",
      confirmButtonClass: "bg-red-500 hover:bg-red-600",
      onConfirm: async () => {
        setConfirm(null);
        setDeleting(true);
        const { error } = await supabase
          .from("practice_bingo_instances")
          .delete()
          .eq("id", attrs.recordId);

        setDeleting(false);
        if (error) return alert(error.message);
        deleteNode?.();
      },
      onClose: () => setConfirm(null),
    });
  };

  const toggleCell = async (cell, el) => {
    if (!saved) return;

    // Allow parent/student interaction even if editor read-only.
    // RLS decides if this update is permitted.
    if (!cell?.cell_id) return;

    // free cell: allow unchecking? usually no. We'll keep it always checked.
    if (cell.is_free) return;

    const nextChecked = cell.checked_at ? null : new Date().toISOString();

    // Subtle wiggle when a square is checked (not when unchecked)
    if (!cell.checked_at && el) {
      requestAnimationFrame(() => cardWiggle(el));
    }

    // Optimistic update
    setCells((prev) =>
      prev.map((c) =>
        c.cell_id === cell.cell_id ? { ...c, checked_at: nextChecked } : c
      )
    );

    const { error } = await supabase
      .from("practice_bingo_instance_cells")
      .update({ checked_at: nextChecked })
      .eq("id", cell.cell_id);

    if (error) {
      // revert if failed
      setCells((prev) =>
        prev.map((c) =>
          c.cell_id === cell.cell_id ? { ...c, checked_at: cell.checked_at } : c
        )
      );
      alert(error.message);
    }
  };

  const gridColsClass = useMemo(() => {
    // Mobile-first: 5x5 -> 3 cols on xs, 5 on sm+ for legibility
    if (gridSize <= 4) return "grid-cols-2 sm:grid-cols-4";
    if (gridSize === 5) return "grid-cols-3 sm:grid-cols-5";
    if (gridSize === 6) return "grid-cols-3 sm:grid-cols-6";
    return "grid-cols-3 sm:grid-cols-7";
  }, [gridSize]);

  const headerBadge = useMemo(() => {
    if (!saved) return null;
    if (status.level === 3)
      return { text: "Full card!", tone: "bg-green-100 text-green-700" };
    if (status.level === 2)
      return { text: "2 lines!", tone: "bg-blue-100 text-blue-700" };
    if (status.level === 1)
      return { text: "Bingo!", tone: "bg-amber-100 text-amber-700" };
    return { text: "In progress", tone: "bg-slate-200 text-slate-700" };
  }, [saved, status.level]);

  const selectedCategoryIds = Array.isArray(settings?.categoryIds)
    ? settings.categoryIds
    : [];

  const SettingsForm = ({ showHeading = false }) => (
    <div className={cls("bg-white rounded-xl p-4 border", !saved && "border-dashed")}>
      {showHeading && (
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-orange-100 text-orange-600 rounded-xl flex items-center justify-center">
              <SafeIcon icon={FiSettings} className="w-5 h-5" />
            </div>
            <div>
              <div className="text-sm font-bold text-slate-800">Settings</div>
              <div className="text-xs text-slate-500">
                Configure the card before the student starts ticking.
              </div>
            </div>
          </div>
          {settingsLocked && (
            <div className="text-xs font-semibold px-2 py-1 rounded-full bg-slate-200 text-slate-700 flex items-center gap-1">
              <SafeIcon icon={FiLock} />
              Locked
            </div>
          )}
        </div>
      )}

      {settingsLocked && (
        <div className="mb-3 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
          The student has started this card, so settings and regeneration are now locked.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* grid size */}
        <div>
          <label className="text-xs font-semibold text-slate-600 mb-1 block">Grid size</label>
          <select
            className="w-full border-slate-300 rounded-lg shadow-sm focus:ring-orange-500 focus:border-orange-500 transition"
            value={String(gridSize)}
            onChange={(e) =>
              persistSettingsToAttrs({
                ...settings,
                gridSize: Number(e.target.value),
              })
            }
            disabled={!canEditTool || settingsLocked}
          >
            <option value="4">4 × 4</option>
            <option value="5">5 × 5</option>
            <option value="6">6 × 6</option>
            <option value="7">7 × 7</option>
          </select>
          {(!canEditTool || settingsLocked) && (
            <div className="text-xs text-slate-500 mt-1">
              <SafeIcon icon={FiLock} className="inline mr-1" />
              {!canEditTool ? "Teachers can change settings." : "Locked after progress starts."}
            </div>
          )}
        </div>

        {/* free center */}
        <div className="flex items-end">
          <label className="flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer select-none">
            <input
              type="checkbox"
              className="h-4 w-4 rounded text-orange-600 border-slate-300 focus:ring-orange-500"
              checked={!!includeFreeCenter}
              onChange={(e) =>
                persistSettingsToAttrs({
                  ...settings,
                  includeFreeCenter: e.target.checked,
                })
              }
              disabled={!canEditTool || settingsLocked}
            />
            Include free center (odd grids only)
          </label>
        </div>

        {/* instrument */}
        <div className="md:col-span-1">
          <label className="text-xs font-semibold text-slate-600 mb-1 block">
            Instrument (optional filter)
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={!canEditTool || settingsLocked}
              className={cls(
                "px-3 py-1.5 rounded-full text-sm border transition-colors",
                !settings?.instrument
                  ? "bg-orange-100 border-orange-200 text-orange-700"
                  : "bg-white border-slate-300 text-slate-700 hover:bg-slate-100",
                (!canEditTool || settingsLocked) && "opacity-60 cursor-not-allowed"
              )}
              onClick={() => persistSettingsToAttrs({ ...settings, instrument: "" })}
              title="No instrument filter"
            >
              All instruments
            </button>

            {instruments.map((inst) => {
              const active = (settings?.instrument || "") === inst;
              return (
                <button
                  key={inst}
                  type="button"
                  disabled={!canEditTool || settingsLocked}
                  className={cls(
                    "px-3 py-1.5 rounded-full text-sm border transition-colors",
                    active
                      ? "bg-orange-100 border-orange-200 text-orange-700"
                      : "bg-white border-slate-300 text-slate-700 hover:bg-slate-100",
                    (!canEditTool || settingsLocked) && "opacity-60 cursor-not-allowed"
                  )}
                  onClick={() =>
                    persistSettingsToAttrs({
                      ...settings,
                      instrument: active ? "" : inst,
                    })
                  }
                >
                  {inst}
                </button>
              );
            })}
          </div>
          <div className="text-xs text-slate-500 mt-2">
            Only instruments that exist in prompts are shown.
          </div>
        </div>

        {/* categories */}
        <div className="md:col-span-2">
          <label className="text-xs font-semibold text-slate-600 mb-2 block">
            Categories (optional)
          </label>
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => {
              const active = selectedCategoryIds.includes(cat.id);
              return (
                <button
                  key={cat.id}
                  type="button"
                  disabled={!canEditTool || settingsLocked}
                  className={cls(
                    "px-3 py-1.5 rounded-full text-sm border transition-colors",
                    active
                      ? "bg-orange-100 border-orange-200 text-orange-700"
                      : "bg-white border-slate-300 text-slate-700 hover:bg-slate-100",
                    (!canEditTool || settingsLocked) && "opacity-60 cursor-not-allowed"
                  )}
                  onClick={() => {
                    const next = active
                      ? selectedCategoryIds.filter((id) => id !== cat.id)
                      : [...selectedCategoryIds, cat.id];
                    persistSettingsToAttrs({
                      ...settings,
                      categoryIds: next,
                    });
                  }}
                >
                  {cat.name}
                </button>
              );
            })}
          </div>
          <div className="text-xs text-slate-500 mt-2">Leave blank to pull from all categories.</div>
        </div>
      </div>
    </div>
  );

  return (
    <NodeViewWrapper
      as="div"
      contentEditable={false}
      role="group"
      tabIndex={-1}
      className="relative group w-full rounded-2xl bg-slate-50 shadow-sm p-4 border border-slate-200"
      onMouseDownCapture={(e) => {
        if (!isFormControl(e.target)) e.preventDefault();
        e.stopPropagation();
      }}
      onTouchStartCapture={(e) => {
        if (!isFormControl(e.target)) e.preventDefault();
        e.stopPropagation();
      }}
      id={attrs.noteBlockId ? `tool-${attrs.noteBlockId}` : undefined}
    >
      <CelebrationOverlay
        show={celebrationOpen}
        message={celebrationMsg || "Nice work! 🎉"}
        duration={2600}
        fullScreen
        intensity="epic"
        onDone={() => setCelebrationOpen(false)}
      />

      {/* Sticker badges (top-right, half outside border) */}
      {stickers.length > 0 && (
        <div className="absolute right-3 top-0 -translate-y-1/2 flex items-center gap-1 z-10">
          {stickers.slice(-10).map((s, idx, arr) => {
            const isNewest = idx === arr.length - 1;
            return (
              <button
                key={s.id || `${s.imageUrl}-${idx}`}
                type="button"
                className="w-9 h-9 rounded-full bg-white border border-slate-200 shadow-sm overflow-hidden flex items-center justify-center hover:shadow transition"
                onClick={() => setStickerLightbox({ imageUrl: s.imageUrl, label: s.label })}
                title={s.label || "Sticker"}
                ref={isNewest ? newestStickerRef : null}
              >
                {/* transparent PNGs should render cleanly */}
                <img
                  src={s.imageUrl}
                  alt={s.label || "Sticker"}
                  className="w-full h-full object-contain"
                  draggable={false}
                />
              </button>
            );
          })}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 mb-4 min-w-0">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-12 h-12 bg-orange-100 text-orange-600 rounded-xl flex items-center justify-center">
            <SafeIcon icon={FiGrid} className="w-6 h-6" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <div className="text-lg font-bold text-slate-800 truncate">
                Practice Bingo
              </div>
              {headerBadge && (
                <div
                  className={cls(
                    "text-xs font-semibold px-2 py-1 rounded-full shrink-0",
                    headerBadge.tone
                  )}
                >
                  {headerBadge.text}
                </div>
              )}
            </div>
            <div className="text-sm text-slate-500">
              Tick off each square as you complete it.
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 flex-wrap sm:flex-nowrap min-w-0">
          {/* Mute/unmute SFX */}
          <button
            className="p-2 rounded-full text-slate-500 hover:bg-slate-200 hover:text-slate-700 transition-colors"
            onClick={() => {
              const next = !sfxMuted;
              setSfxMuted(next);
              updateAttributes({ sfxMuted: next });
            }}
            title={sfxMuted ? "Unmute celebration sound" : "Mute celebration sound"}
          >
            <SafeIcon icon={sfxMuted ? FiVolumeX : FiVolume2} className="w-5 h-5" />
          </button>

          {/* Settings icon */}
          <button
            className={cls(
              "p-2 rounded-full transition-colors",
              settingsLocked || !canEditTool
                ? "text-slate-300 cursor-not-allowed"
                : "text-slate-500 hover:bg-slate-200 hover:text-slate-700"
            )}
            onClick={() => {
              if (!canEditTool || settingsLocked) return;
              setSettingsOpen(true);
            }}
            title={
              !canEditTool
                ? "Teachers only"
                : settingsLocked
                ? "Locked after progress starts"
                : "Settings"
            }
          >
            <SafeIcon icon={FiSettings} className="w-5 h-5" />
          </button>

          {/* Save (teacher only) */}
          <button
            className={cls(
              "px-2.5 py-1.5 sm:px-3 rounded-lg text-sm font-semibold border transition-all flex items-center gap-2 whitespace-nowrap max-w-full",
              saved
                ? "bg-slate-200 text-slate-500 border-slate-200 cursor-default"
                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100 hover:border-slate-400"
            )}
            disabled={!canEditTool || saving || saved}
            onClick={handleSave}
            title={
              canEditTool
                ? saved
                  ? "Saved"
                  : "Save this tool"
                : "Teachers must save this tool"
            }
          >
            <SafeIcon icon={canEditTool ? FiSave : FiLock} />
            {saved ? "Saved" : saving ? "Saving..." : "Save"}
          </button>

          {/* Regenerate (teacher only) */}
          <button
            className={cls(
              "px-2.5 py-1.5 sm:px-3 rounded-lg text-sm font-semibold border transition-all flex items-center gap-2 whitespace-nowrap max-w-full",
              !saved || !canEditTool || settingsLocked
                ? "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed"
                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100 hover:border-slate-400"
            )}
            disabled={!saved || !canEditTool || generating || settingsLocked}
            onClick={() => {
              if (settingsLocked) return;
              setConfirm({
                title: "Regenerate Bingo Card?",
                message:
                  "This will create a new bingo card and reset all checkmarks for this tool.",
                confirmText: "Regenerate",
                confirmButtonClass: "bg-orange-500 hover:bg-orange-600",
                onConfirm: async () => {
                  setConfirm(null);
                  try {
                    await persistInstanceSettings(attrs.recordId);
                  } catch (e) {
                    return alert(e?.message || "Failed to update settings.");
                  }
                  await handleGenerate(attrs.recordId, true);
                },
                onClose: () => setConfirm(null),
              });
            }}
            title={
              !canEditTool
                ? "Teachers only"
                : !saved
                ? "Save first"
                : settingsLocked
                ? "Locked after progress starts"
                : "Regenerate"
            }
          >
            <SafeIcon icon={FiRefreshCw} />
            {generating ? "Working..." : "Regenerate"}
          </button>

          {/* Delete (teacher only) */}
          {canDelete && (
            <button
              className="p-2 rounded-full text-slate-500 hover:bg-red-100 hover:text-red-600 transition-colors opacity-50 group-hover:opacity-100"
              disabled={deleting}
              onClick={handleDelete}
              title="Delete Tool"
            >
              <SafeIcon icon={FiTrash2} className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Settings modal */}
      {settingsOpen && (
        <div data-mms-modal>
          {/* Proper modal (single overlay) */}
          <div
            className="fixed inset-0 z-[1000] flex items-center justify-center p-4"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) setSettingsOpen(false);
            }}
          >
            <div className="absolute inset-0 bg-black/40" />
            <div className="relative bg-white rounded-2xl max-w-2xl w-full p-4 sm:p-5 border border-slate-200 shadow-xl">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                    <SafeIcon icon={FiSettings} className="text-orange-600" />
                  </div>
                  <div>
                    <div className="text-lg font-bold text-slate-800">Bingo Settings</div>
                    <div className="text-sm text-slate-500">
                      Teachers: adjust filters and card size.
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  className="p-2 rounded-xl hover:bg-slate-100"
                  onClick={() => setSettingsOpen(false)}
                  title="Close"
                >
                  <SafeIcon icon={FiX} className="text-slate-500" />
                </button>
              </div>

              <SettingsForm />

              <div className="mt-4 flex items-center justify-end gap-2">
                <button
                  type="button"
                  className="px-4 py-2 rounded-xl border border-slate-300 bg-white hover:bg-slate-100 text-slate-700 font-semibold"
                  onClick={() => setSettingsOpen(false)}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Body */}
      {!saved ? (
        <div className="space-y-3">
          {/* Inline settings immediately visible on add */}
          <SettingsForm showHeading />
          <div className="text-xs text-slate-500">
            After you press <b>Save</b>, parents/students can tick squares. Once ticking begins, settings
            and regeneration are locked.
          </div>
        </div>
      ) : (
        <>
          {/* Inline settings available again until first tick */}
          {canEditTool && !settingsLocked && (
            <div className="mb-4">
              <SettingsForm showHeading />
            </div>
          )}

          {loadError && (
            <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
              {loadError}
            </div>
          )}

          {/* Status bar */}
          <div className="mb-3 flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 text-sm text-slate-700">
              <SafeIcon icon={FiAward} className="text-slate-400" />
              <span>
                Lines completed: <b>{status.completedLines}</b>
                {status.fullCard ? " · Full card complete!" : ""}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {status.level > 0 && (
                <div className="flex items-center gap-2 text-sm font-semibold text-green-700 bg-green-100 px-3 py-1.5 rounded-full">
                  <SafeIcon icon={FiCheckCircle} />
                  {status.level === 3
                    ? "Full card!"
                    : status.level === 2
                    ? "Two lines!"
                    : "Bingo!"}
                </div>
              )}
            </div>
          </div>

          {/* Grid */}
          <div className="p-2 sm:p-4 bg-slate-200/50 rounded-xl">
            <div className={cls("grid gap-2 sm:gap-3", gridColsClass)}>
              {cells.map((cell) => {
                const checked = !!cell.checked_at || !!cell.is_free;
                return (
                  <button
                    key={cell.cell_id}
                    type="button"
                    className={cls(
                      "text-left rounded-xl border shadow-sm transition-all p-3 sm:p-3.5 min-h-[72px] sm:min-h-[88px] flex flex-col justify-between",
                      checked
                        ? "bg-gradient-to-br from-orange-500 to-amber-500 border-orange-400 hover:from-orange-600 hover:to-amber-600"
                        : "bg-white border-slate-200 hover:bg-slate-50",
                      cell.is_free && "ring-2 ring-orange-200"
                    )}
                    onClick={(e) => toggleCell(cell, e.currentTarget)}
                    disabled={generating}
                    title={cell.is_free ? "Free space" : checked ? "Uncheck" : "Check"}
                  >
                    <div
                      className={cls(
                        "text-sm font-semibold leading-snug",
                        checked ? "text-white" : "text-slate-800"
                      )}
                    >
                      {cell.title || (cell.is_free ? "FREE" : "")}
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <div className={cls("text-xs", checked ? "text-white/80" : "text-slate-400")}>
                        #{Number(cell.cell_position) + 1}
                      </div>
                      <div
                        className={cls(
                          "w-6 h-6 rounded-full border flex items-center justify-center",
                          checked
                            ? "bg-white/20 border-white/30 text-white"
                            : "bg-white border-slate-300 text-slate-300"
                        )}
                      >
                        <SafeIcon
                          icon={FiCheckCircle}
                          className={cls("w-4 h-4", checked ? "opacity-95" : "")}
                        />
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Confirm modal (delete/regenerate) */}
      {confirm && (
        <div data-mms-modal>
          <ConfirmModal
            title={confirm.title}
            message={confirm.message}
            confirmText={confirm.confirmText}
            confirmButtonClass={confirm.confirmButtonClass}
            onConfirm={confirm.onConfirm}
            onClose={confirm.onClose}
          />
        </div>
      )}

      {/* Sticker lightbox */}
      {stickerLightbox && (
        <div
          data-mms-modal
          className="fixed inset-0 z-[1000] flex items-center justify-center p-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setStickerLightbox(null);
          }}
        >
          <div className="absolute inset-0 bg-black/60" />
          <div className="relative bg-white rounded-2xl max-md w-full p-4 border border-slate-200 shadow-xl">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-bold text-slate-800">
                {stickerLightbox.label || "Sticker"}
              </div>
              <button
                type="button"
                className="p-2 rounded-xl hover:bg-slate-100"
                onClick={() => setStickerLightbox(null)}
                title="Close"
              >
                <SafeIcon icon={FiX} className="text-slate-500" />
              </button>
            </div>
            <div className="w-full flex items-center justify-center p-4">
              <img
                src={stickerLightbox.imageUrl}
                alt={stickerLightbox.label || "Sticker"}
                className="max-h-[320px] w-auto object-contain"
                draggable={false}
              />
            </div>
          </div>
        </div>
      )}
    </NodeViewWrapper>
  );
}

const PracticeBingoNode = Node.create({
  name: "practiceBingo",
  group: "block",
  atom: true,
  selectable: true,
  addAttributes() {
    return {
      noteBlockId: { default: null },
      lessonNoteId: { default: null },
      schoolId: { default: null },
      studentMasterId: { default: null },
      recordId: { default: null },
      settings: { default: DEFAULT_SETTINGS },
      sfxMuted: { default: false },
    };
  },
  parseHTML() {
    return [{ tag: "practice-bingo" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["practice-bingo", mergeAttributes(HTMLAttributes)];
  },
  addNodeView() {
    return ReactNodeViewRenderer(PracticeBingoBlock);
  },
});

export default PracticeBingoNode;